import { useGetAgencyOrganizations, useUpdateOrganizations } from 'apis/agency/organizations/hook';
import { Box, Typography, Paper, Stack, InputAdornment } from '@mui/material';
import Button from 'components/Common/Button';
import UploadIcon from 'assets/icons/UploadIcon';
import Input from 'components/Common/Input';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Grid from '@mui/material/Grid2';
import { CiGlobe } from 'react-icons/ci';
import PreviewDomainIcon from '@/assets/icons/PreviewDomainIcon';
import { AgencyOrganizationsResponse } from '@/apis/agency/organizations/types';
import { brandingSchema } from '@/utils/schema/validator/AgencyConfigureValidation';

export default function Branding() {
  const { data: organizations } = useGetAgencyOrganizations();
  const { mutateAsync: updateOrganizations, isPending } = useUpdateOrganizations();
  const [logo, setLogo] = useState<File | null>(null);
  const [favIcon, setFavIcon] = useState<File | null>(null);

  const { control, handleSubmit } = useForm<AgencyOrganizationsResponse>({
    resolver: zodResolver(brandingSchema),
    values: organizations
  });

  const onUpload = (e: React.ChangeEvent<HTMLInputElement>, type?: string) => {
    if (e.target.files?.[0]) {
      const file = e.target.files[0];
      type === 'logo' ? setLogo(file) : setFavIcon(file);
    }
  };

  const onSubmit = async (data: AgencyOrganizationsResponse) => {
    const formData = new FormData();
    formData.append('organization[name]', data.name);
    formData.append('organization[support_email]', data.support_email);
    const supportCalendar = data.configuration?.support_calendar?.trim();
    if (supportCalendar) {
      formData.append('organization[configuration][support_calendar]', supportCalendar);
    }
    data?.configuration?.brand_color &&
      formData.append('organization[configuration][brand_color]', data.configuration.brand_color);
    logo && formData.append('organization[logo]', logo);
    favIcon && formData.append('organization[favicon]', favIcon);
    data?.configuration?.terms_and_conditions_url &&
      formData.append(
        'organization[configuration][terms_and_conditions_url]',
        data.configuration.terms_and_conditions_url
      );
    data?.configuration?.privacy_policy_url &&
      formData.append('organization[configuration][privacy_policy_url]', data.configuration.privacy_policy_url);

    await updateOrganizations(formData);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={{ xs: 2, lg: 3 }}>
        <Grid size={12}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, lg: 3 },
              backgroundColor: '#fff',
              borderRadius: 2,
              border: '1px solid #E0E0E0'
            }}
          >
            <Grid container spacing={{ xs: 2, lg: 3 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Input name='name' control={control} fullWidth label='Business Name' />
                <Typography variant='caption' color='text.secondary' mt={0.5} display='block'>
                  This name will appear throughout the application and in communications.
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Input name='support_email' control={control} fullWidth label='Support Email' />
                <Typography variant='caption' color='text.secondary' mt={0.5} display='block'>
                  This email will appear throughout the application and in communications.
                </Typography>
              </Grid>
              <Grid size={12}>
                <Input
                  name='configuration.support_calendar'
                  control={control}
                  fullWidth
                  label='Support Calendar'
                  placeholder='https://calendly.com/your-link'
                />
                <Typography variant='caption' color='text.secondary' mt={0.5} display='block'>
                  This link will be displayed after the user completes the onboarding form and reaches the thank you
                  page.
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }} border='1px solid #ECECEC' p={{ xs: 1.5, lg: 2 }} borderRadius={2}>
                <Typography fontSize={20} fontWeight={500} mb={1} sx={{ fontSize: { xs: '1rem', lg: '1.25rem' } }}>
                  Logo
                </Typography>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 2, sm: 3 }}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                >
                  {logo || organizations?.logo_url ? (
                    <Box
                      sx={{
                        width: { xs: 80, lg: 100 },
                        height: { xs: 80, lg: 100 },
                        borderRadius: 1,
                        overflow: 'hidden',

                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',

                        flexShrink: 0
                      }}
                    >
                      <img
                        src={logo ? URL.createObjectURL(logo) : organizations?.logo_url}
                        alt='Logo'
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      />
                    </Box>
                  ) : (
                    <Box
                      width={{ xs: 80, lg: 100 }}
                      height={{ xs: 80, lg: 100 }}
                      border='1px solid #E0E0E0'
                      borderRadius={1}
                      display='flex'
                      alignItems='center'
                      justifyContent='center'
                      bgcolor='#F5F5F5'
                      flexShrink={0}
                    >
                      <Typography
                        variant='h6'
                        fontWeight={500}
                        color='text.secondary'
                        sx={{ fontSize: { xs: '0.875rem', lg: '1.25rem' } }}
                      >
                        Logo
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <label htmlFor='logo-upload'>
                      <input
                        accept='image/*'
                        type='file'
                        name='logo_url'
                        hidden
                        id='logo-upload'
                        onChange={(e) => onUpload(e, 'logo')}
                      />
                      <Button variant='outlined' component='span' startIcon={<UploadIcon />} size='small'>
                        Update Logo
                      </Button>
                    </label>
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      mt={1}
                      display='block'
                      sx={{ fontSize: { xs: '11px', lg: '0.75rem' } }}
                    >
                      Recommended file size is 2MB. (only .jpg and .png files)
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }} border='1px solid #ECECEC' p={{ xs: 1.5, lg: 2 }} borderRadius={2}>
                <Typography fontSize={20} fontWeight={500} mb={1} sx={{ fontSize: { xs: '1rem', lg: '1.25rem' } }}>
                  Favicon
                </Typography>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 2, sm: 3 }}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                >
                  {favIcon || organizations?.favicon_url ? (
                    <Box
                      sx={{
                        width: { xs: 80, lg: 100 },
                        height: { xs: 80, lg: 100 },
                        borderRadius: 1,
                        overflow: 'hidden',

                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',

                        flexShrink: 0
                      }}
                    >
                      <img
                        src={favIcon ? URL.createObjectURL(favIcon) : organizations?.favicon_url}
                        alt='Favicon'
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      />
                    </Box>
                  ) : (
                    <Box
                      width={{ xs: 80, lg: 100 }}
                      height={{ xs: 80, lg: 100 }}
                      border='1px solid #E0E0E0'
                      borderRadius={1}
                      display='flex'
                      alignItems='center'
                      justifyContent='center'
                      bgcolor='#F5F5F5'
                      flexShrink={0}
                    >
                      <Typography
                        variant='h6'
                        fontWeight={500}
                        color='text.secondary'
                        sx={{ fontSize: { xs: '0.875rem', lg: '1.25rem' } }}
                      >
                        Fav Icon
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <label htmlFor='favicon-upload'>
                      <input
                        accept='image/*'
                        type='file'
                        name='favicon_url'
                        hidden
                        id='favicon-upload'
                        onChange={(e) => onUpload(e, 'favIcon')}
                      />
                      <Button variant='outlined' component='span' startIcon={<UploadIcon />} size='small'>
                        Update Favicon
                      </Button>
                    </label>
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      mt={1}
                      display='block'
                      sx={{ fontSize: { xs: '11px', lg: '0.75rem' } }}
                    >
                      Recommended file size is 2MB. (only .ico and .png files)
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box
                  display='flex'
                  flexDirection={{ xs: 'column', sm: 'row' }}
                  alignItems={{ xs: 'flex-start', sm: 'start' }}
                  gap={2}
                >
                  <Box width={{ xs: '100%', sm: 58 }} sx={{ flexShrink: 0 }}>
                    <Input
                      name='configuration.brand_color'
                      defaultValue={'#4B22A8'}
                      control={control}
                      type='color'
                      fullWidth
                      inputProps={{ style: { padding: 0, height: 58 } }}
                    />
                  </Box>
                  <Box
                    display='flex'
                    flexDirection='column'
                    alignItems='start'
                    sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}
                  >
                    <Input
                      name='configuration.brand_color'
                      control={control}
                      defaultValue={'#4B22A8'}
                      placeholder='#9b87f5'
                      size='small'
                      label='Platform Primary Color'
                      sx={{
                        width: { xs: '100%', sm: '200px' },
                        input: {
                          fontFamily: 'monospace'
                        }
                      }}
                    />
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      mt={0.5}
                      display='block'
                      sx={{ fontSize: { xs: '11px', lg: '0.75rem' } }}
                    >
                      Enter a valid hex code (e.g., #9b87f5)
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid size={12}>
                <Box display='flex' flexDirection='column' alignItems='start'>
                  <Input
                    name='configuration.terms_and_conditions_url'
                    control={control}
                    fullWidth
                    label='Terms & Conditions URL'
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <CiGlobe size={18} />
                        </InputAdornment>
                      )
                    }}
                  />
                  <Typography variant='caption' color='text.secondary' mt={0.5} display='block'>
                    This link will be shown on the login page
                  </Typography>
                </Box>
              </Grid>
              <Grid size={12}>
                <Box display='flex' flexDirection='column' alignItems='start'>
                  <Input
                    name='configuration.privacy_policy_url'
                    control={control}
                    fullWidth
                    label='Privacy Policy URL'
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <CiGlobe size={18} />
                        </InputAdornment>
                      )
                    }}
                  />
                  <Typography variant='caption' color='text.secondary' mt={0.5} display='block'>
                    This link will be shown on the login page
                  </Typography>
                </Box>
              </Grid>
              <Grid size={12}>
                <Box display='flex' flexDirection='column' alignItems='start'>
                  <Input
                    name='domain'
                    control={control}
                    variant='outlined'
                    label='Custom Domain'
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <PreviewDomainIcon />
                        </InputAdornment>
                      ),
                      disabled: true,
                      readOnly: true
                    }}
                  />
                  <Typography variant='caption' color='text.secondary' mt={0.5} display='block'>
                    This is how your domain will appear to customers. For change, please connect to support team.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      <Box mt={{ xs: 2, lg: 3 }} display='flex' justifyContent='flex-end'>
        <Button
          variant='contained'
          type='submit'
          size='large'
          fullWidth
          isPending={isPending}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Save Branding
        </Button>
      </Box>
    </form>
  );
}
